import store from 'utils/store/withMiddleware'
import produce from 'immer'
import fitPlanConfigurationForFilter from 'sites/pivothealth.com/short-term-health-insurance/consumer/quotes/utils/fitPlanConfigurationForFilter'

type PlanConditionValue = number | string
type PlanConditionObject = { [key: string]: PlanConditionValue | PlanConditionValue[] }
type PlanCondition = PlanConditionObject | string

interface OptionConfigs {
  deductible?: {
    selected: number
    values: number[]
  }
  coinsurance?: {
    selected: string
    values: string[]
  }
  coverage?: {
    selected: number
    values: number[]
  }
  period?: {
    selected: number
    values: string[]
  }
  moop?: {
    selected: number
    values: number[]
  }
}

export interface CustomFilterProps {
  agents: number[]
  planConditions: PlanCondition[]
  expectedProductIds: number[]
  overrideOptionsByProduct: { [productName: string]: OptionConfigs }
}
/**
 * Filter the plans based on the conditions received, it will match the plan's type when the condition is a string.
 * A condition object will be valid if all its entries matches the same key/values in the plan object,
 * if the value of a condition key is an array it will only check for the `Array.includes` result.
 *
 * Example:
 * [{ type: 'epic', planTypeKey: ['epic', 'epic-rx'] }]
 *
 * For the previous example, all the plans with type='epic' **and** a planTypeKey value such as 'epic' OR 'epic-rx' will be valid plans.
 * @param state
 * @param planConditions
 */
const filterPlans = (state: Pivot.State, planConditions: PlanCondition[]): number[] => {
  return state.plansSTM.filter(key => {
    const plan = state.plans[key]

    return planConditions.some(condition => {
      // process simple condition
      if (typeof condition === 'string') {
        return condition === plan.type
      }

      // process condition object
      return Object.keys(condition).every(key => {
        if (Array.isArray(condition[key])) {
          return (condition[key] as PlanConditionValue[]).includes(plan[key])
        } else {
          return condition[key] === plan[key]
        }
      })
    })
  })
}

const filterAddOns = (state: Pivot.State, productIds: number[]): number[] => {
  return state.plansAddOn.filter(key => productIds.includes(state.plans[key].productId))
}

const getProductIdsToRemove = (
  state: Pivot.State,
  expectedProductIds: number[]
): number[] => {
  const productIds = new Set(state.plansAddOn.map(key => state.plans[key].productId))
  return [...productIds].filter(productId => !expectedProductIds.includes(productId))
}

const filterPreselectedAddons = (state: Pivot.State, productIds: number[]) => {
  return Object.fromEntries(
    Object.entries(state.addonsByPlan).map(([planKey, addonKeys]) => {
      const filteredAddonKeys = addonKeys.filter(addonKey =>
        productIds.includes(state.plans[addonKey].productId)
      )
      return [planKey, filteredAddonKeys]
    })
  )
}

const overrideFiltersByProductType = (
  state: Pivot.State,
  overrideOptionsByProduct: { [productName: string]: OptionConfigs }
) => {
  return produce(state.filtersByProductType, draft => {
    Object.entries(overrideOptionsByProduct).forEach(([productName, optionConfigs]) => {
      Object.entries(optionConfigs).forEach(([optionConfigKey, config]) => {
        if (draft[productName]?.[optionConfigKey] === undefined) return
        draft[productName][optionConfigKey] = config.selected
      })
    })
  })
}

const overrideFilterOptionsByProductType = (
  state: Pivot.State,
  overrideOptionsByProduct: { [productName: string]: OptionConfigs }
) => {
  return produce(state.filterOptionsByProductType, draft => {
    Object.entries(overrideOptionsByProduct).forEach(([productName, optionConfigs]) => {
      Object.entries(optionConfigs).forEach(([optionConfigKey, config]) => {
        const productFilter = draft[productName]?.[optionConfigKey]
        if (productFilter === undefined) return
        productFilter.values = productFilter.values.filter(v => config.values.includes(v))
      })
    })
  })
}

const onlyUnique = <T>(value: T, index: number, array: T[]) => {
  // useful for small array
  return array.indexOf(value) === index
}

const updateFilterOptions = (
  state: Pivot.State,
  filteredPlans: number[],
  filterOptionsByProductType: { [productName: string]: Pivot.ProductFilters }
) => {
  return produce(state.filterOptions, draft => {
    // plan type
    draft.type.values = filteredPlans
      .map(key => state.plans[key].type)
      .filter(Boolean)
      .filter(onlyUnique)

    // network provider
    draft.networkProvider.values = filteredPlans
      .map(key => state.plans[key].coreBenefits.networkProvider)
      .filter(Boolean)
      .filter(onlyUnique)

    // Deductible
    draft.deductible.options = filteredPlans
      .map(planKey => state.plans[planKey].type)
      .filter(onlyUnique)
      .map(type => filterOptionsByProductType[type]?.deductible?.values)
      .filter(x => x != null)
      .flat()
      .filter(onlyUnique)

    // coverage
    draft.coverage.options = filteredPlans
      .map(planKey => state.plans[planKey].type)
      .filter(onlyUnique)
      .map(type => filterOptionsByProductType[type]?.coverage?.values)
      .filter(x => x != null)
      .flat()
      .filter(onlyUnique)
  })
}

const overridePlansByType = (state: Pivot.State, expectedProductIds: number[]) => {
  const productIdsToRemove = getProductIdsToRemove(state, expectedProductIds)

  return produce(state.plansByType, draft => {
    productIdsToRemove.forEach(productId => {
      draft[productId] = []
    })
  })
}

/**
 * Applies filtering based on the args provided
 *
 * @example
 * planConditions: [{ type: 'epic', planTypeKey: ['epic', 'epic-rx'] }]
 *
 * @example
 * overrideOptionsByProduct:
 * {
 *   core: {
 *     deductible: {
 *       selected: 1000,
 *       values: [1000]
 *     },
 *     coinsurance: {
 *       selected: '20',
 *       values: ['20']
 *     },
 *   }
 * }
 *
 * @param agents
 * @param planConditions Array of conditions, matches the plan's type when the condition is a string otherwise it will match the object.
 * @param expectedProductIds
 * @param overrideOptionsByProduct Overrides the different plan options
 */
export function customFilters({
  agents,
  planConditions,
  expectedProductIds,
  overrideOptionsByProduct = {}
}: CustomFilterProps) {
  // generates a deep clone of state
  const state = store.getState()

  if (!agents.includes(state.hcStorage.agent_id)) return

  const filteredPlans = filterPlans(state, planConditions)

  const filteredAddOns = filterAddOns(state, expectedProductIds)

  // addon hiding
  const modifiedPlansByType = overridePlansByType(state, expectedProductIds)

  // remove preselected addons if not expected
  const modifiedAddonsByPlan = filterPreselectedAddons(state, expectedProductIds)

  // overrides available options each plan has
  const modifiedFilterOptionsByProductType = overrideFilterOptionsByProductType(
    state,
    overrideOptionsByProduct
  )

  // set plan's selected options
  const modifiedFiltersByProductType = overrideFiltersByProductType(
    state,
    overrideOptionsByProduct
  )

  const newFiltersByProductType = Object.keys(modifiedFiltersByProductType).reduce(
    (accum, key) => {
      return { ...accum, [key]: { ...modifiedFiltersByProductType[key] } }
    },
    {}
  )

  Object.keys(modifiedFiltersByProductType).forEach(planTypeKey => {
    const typeConfiguration = overrideOptionsByProduct[planTypeKey]
    if (typeConfiguration) {
      const filters = Object.keys(typeConfiguration).map(benefitKey => {
        return {
          filterName: benefitKey,
          filterValue: newFiltersByProductType[planTypeKey][benefitKey]
        }
      })

      const planFilters = fitPlanConfigurationForFilter({
        planTypeKey,
        filters,
        filtersByProductType: newFiltersByProductType
      }).planFilter

      newFiltersByProductType[planTypeKey] = {
        ...planFilters
      }
    }
  })

  // updates header filters
  const modifiedFilterOptions = updateFilterOptions(
    state,
    filteredPlans,
    modifiedFilterOptionsByProductType
  )

  store.update({
    filterOptions: modifiedFilterOptions,
    plansSTM: filteredPlans,
    plansAddOn: filteredAddOns,
    plansByType: modifiedPlansByType,
    filterOptionsByProductType: modifiedFilterOptionsByProductType,
    filtersByProductType: newFiltersByProductType,
    addonsByPlan: modifiedAddonsByPlan,
    recommended: [],
    showRecommended: false,
    showRecommend: false
  })
}

export function advancedCustomFilters(
  agents: number[],
  planConditions: PlanCondition[],
  expectedProductIds: number[],
  overrideOptionsByProduct: { [productName: string]: OptionConfigs },
  benefitRules: [{ benefit: string; validation: (benefitValue) => boolean }]
) {
  const { filterOptionsByProductType } = store.getState()

  Object.keys(filterOptionsByProductType).forEach(planTypeKey => {
    const filter = overrideOptionsByProduct[planTypeKey]
    if (filter) {
      benefitRules.forEach(benefitRule => {
        overrideOptionsByProduct[planTypeKey][benefitRule.benefit].values =
          filterOptionsByProductType[planTypeKey][benefitRule.benefit].values.filter(
            benefitRule.validation
          )
      })
    }
  })

  customFilters({ agents, planConditions, expectedProductIds, overrideOptionsByProduct })
}
